import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Bell, ChevronDown, ChevronUp, ExternalLink, CheckCheck, CalendarClock, AlertTriangle, Syringe, CalendarRange, Plus, Minus } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { echeancesApi, planningApi } from '../../utils/api';

const SESSION_KEY = 'georges_session';

function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = 'sess_' + Date.now() + '_' + Math.random().toString(36).slice(2);
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

// Signature « déjà vu » des nouveautés, persistée par utilisateur.
// Sert à n'auto-ouvrir la pop-up que si un élément est nouveau depuis la dernière consultation.
function seenKey(sessionId) {
  return `georges_nouveautes_vues_${sessionId}`;
}
function loadSeen(sessionId) {
  try { return new Set(JSON.parse(localStorage.getItem(seenKey(sessionId)) || '[]')); }
  catch { return new Set(); }
}
function saveSeen(sessionId, set) {
  try { localStorage.setItem(seenKey(sessionId), JSON.stringify([...set].slice(-300))); }
  catch { /* quota, ignore */ }
}

const TYPE_STYLES = {
  success: {
    bg: 'bg-emerald-50 border-emerald-300',
    icon: '✅',
    badge: 'bg-emerald-100 text-emerald-700',
    bar: 'bg-emerald-400',
    dot: 'bg-emerald-500',
  },
  warning: {
    bg: 'bg-amber-50 border-amber-300',
    icon: '⚠️',
    badge: 'bg-amber-100 text-amber-700',
    bar: 'bg-amber-400',
    dot: 'bg-amber-500',
  },
  danger: {
    bg: 'bg-red-50 border-red-300',
    icon: '🚨',
    badge: 'bg-red-100 text-red-700',
    bar: 'bg-red-400',
    dot: 'bg-red-500',
  },
  info: {
    bg: 'bg-navy-50 border-navy-300',
    icon: 'ℹ️',
    badge: 'bg-navy-100 text-navy-700',
    bar: 'bg-navy-400',
    dot: 'bg-navy-500',
  },
};

const CAT_LABELS = {
  reglementaire: 'Réglementaire',
  financement: 'Financement',
  sante: 'Santé',
  rh: 'RH',
  fiscal: 'Fiscal',
  autre: 'Autre',
};

function AlerteCard({ alerte, onLue, expanded, onToggle }) {
  const style = TYPE_STYLES[alerte.type] || TYPE_STYLES.info;

  return (
    <div className={`border rounded-xl overflow-hidden shadow-sm transition-all ${style.bg}`}>
      {/* Barre colorée en haut */}
      <div className={`h-1 w-full ${style.bar}`} />

      <div className="px-4 py-3">
        {/* Header */}
        <div className="flex items-start gap-3">
          <span className="text-xl flex-shrink-0 mt-0.5">{style.icon}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  {alerte.epingle === 1 && (
                    <span className="text-xs bg-slate-800 text-white px-2 py-0.5 rounded-full font-medium">📌 Épinglé</span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${style.badge}`}>
                    {CAT_LABELS[alerte.categorie] || alerte.categorie}
                  </span>
                  {alerte.date_effet && (
                    <span className="text-xs text-slate-500">
                      Applicable le {new Date(alerte.date_effet).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </div>
                <p className="font-semibold text-slate-800 text-sm leading-snug">{alerte.titre}</p>
              </div>
              <button
                onClick={onToggle}
                className="text-slate-400 hover:text-slate-600 flex-shrink-0 mt-0.5"
              >
                {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>
          </div>
        </div>

        {/* Contenu expandable */}
        {expanded && (
          <div className="mt-3 ml-8 space-y-3">
            <p className="text-sm text-slate-600 leading-relaxed">{alerte.contenu}</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {alerte.source && (
                  <span className="text-xs text-slate-400">
                    Source : <strong>{alerte.source}</strong>
                  </span>
                )}
                {alerte.source_url && (
                  <a
                    href={alerte.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-navy-500 hover:text-navy-800 flex items-center gap-1"
                  >
                    Voir la source <ExternalLink size={11} />
                  </a>
                )}
              </div>
              <button
                onClick={onLue}
                className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
              >
                <CheckCheck size={13} /> Marquer lu
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AlertesBanner() {
  const { user } = useAuth();
  const { selectedStructure } = useApp();
  const [alertes, setAlertes] = useState([]);
  const [echeances, setEcheances] = useState([]);
  const [planningMods, setPlanningMods] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  // « Lu » lié à l'utilisateur connecté (persistant, multi-appareils) ; repli session pour les cas sans compte.
  const sessionId = user ? `u${user.id}` : getSessionId();
  const [shown, setShown] = useState(false);
  // Clés des nouveautés actuellement affichées (pour marquer « vu » à l'ouverture).
  const currentKeysRef = useRef([]);

  const load = useCallback(async () => {
    try {
      const structParams = selectedStructure ? { structure_id: selectedStructure } : {};
      const [aRes, eRes, pRes] = await Promise.all([
        axios.get(`/api/alertes/non-lues?session_id=${sessionId}`),
        echeancesApi.getAll(structParams).catch(() => ({ data: [] })),
        planningApi.getModifications(structParams).catch(() => ({ data: [] })),
      ]);
      const nvAlertes = aRes.data || [];
      const ech = (eRes.data || []).filter(e => e.niveau === 'urgent' || e.niveau === 'depasse');
      const mods = pRes.data || [];
      setAlertes(nvAlertes);
      setEcheances(ech);
      setPlanningMods(mods);

      // Signature des éléments actuellement présents (uniquement ceux qui peuvent déclencher l'ouverture).
      const declencheurs = [
        ...nvAlertes.filter(a => a.epingle).map(a => `a${a.id}`),
        ...ech.map(e => `e${e.id}`),
        ...mods.map(m => `p${m.id}`),
      ];
      currentKeysRef.current = declencheurs;
      const seen = loadSeen(sessionId);
      const duNouveau = declencheurs.some(k => !seen.has(k));
      // Auto-ouvrir au plus une fois par session, et seulement si quelque chose a bougé depuis la dernière lecture.
      if (!shown && duNouveau) {
        setIsOpen(true);
        setShown(true);
      }
    } catch { /* silencieux */ }
  }, [sessionId, selectedStructure, shown]);

  useEffect(() => { load(); }, [load]);

  // Dès que la pop-up est ouverte (auto ou manuellement), on considère les nouveautés affichées comme vues.
  useEffect(() => {
    if (!isOpen) return;
    const seen = loadSeen(sessionId);
    currentKeysRef.current.forEach(k => seen.add(k));
    saveSeen(sessionId, seen);
  }, [isOpen, sessionId, alertes, echeances, planningMods]);

  const marquerLue = async (id) => {
    await axios.post(`/api/alertes/${id}/lue`, { session_id: sessionId });
    setAlertes(prev => prev.filter(a => a.id !== id));
  };

  const marquerToutesLues = async () => {
    await axios.post('/api/alertes/toutes-lues', { session_id: sessionId });
    setAlertes([]);
  };

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const nbNonLues = alertes.length;
  const nbTotal = nbNonLues + echeances.length + planningMods.length;

  if (nbTotal === 0 && !isOpen) return null;

  return (
    <>
      {/* Bouton cloche flottant */}
      {nbTotal > 0 && !isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed top-4 right-6 z-40 flex items-center gap-2 bg-white border border-slate-200 shadow-lg rounded-full px-4 py-2 hover:shadow-xl transition-shadow group"
        >
          <div className="relative">
            <Bell size={18} className="text-slate-600 group-hover:text-navy-800 transition-colors" />
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-bounce">
              {nbTotal}
            </span>
          </div>
          <span className="text-sm font-medium text-slate-700">
            {nbTotal} nouveauté{nbTotal > 1 ? 's' : ''}
          </span>
        </button>
      )}

      {/* Panel alertes */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4" onClick={(e) => { if (e.target === e.currentTarget) setIsOpen(false); }}>
          <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-slate-800 to-slate-700">
              <div className="flex items-center gap-3">
                <Bell size={20} className="text-white" />
                <div>
                  <p className="text-white font-semibold">Nouveautés</p>
                  <p className="text-slate-300 text-xs">
                    {nbNonLues} alerte{nbNonLues > 1 ? 's' : ''} · {echeances.length} échéance{echeances.length > 1 ? 's' : ''} · {planningMods.length} planning
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {nbNonLues > 0 && (
                  <button
                    onClick={marquerToutesLues}
                    className="text-xs text-slate-300 hover:text-white flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10 transition-colors"
                  >
                    <CheckCheck size={13} /> Tout marquer lu
                  </button>
                )}
                <button onClick={() => setIsOpen(false)} className="text-slate-300 hover:text-white p-1 rounded transition-colors">
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Liste des nouveautés */}
            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {alertes.length === 0 && echeances.length === 0 && planningMods.length === 0 ? (
                <div className="text-center py-10 text-slate-400">
                  <CheckCheck size={32} className="mx-auto mb-2 text-green-300" />
                  <p className="text-sm">Rien à signaler — vous êtes à jour !</p>
                </div>
              ) : (
                <>
                  {alertes.length > 0 && (
                    <div className="space-y-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Alertes réglementaires</div>
                      {alertes.map(alerte => (
                        <AlerteCard
                          key={alerte.id}
                          alerte={alerte}
                          onLue={() => marquerLue(alerte.id)}
                          expanded={!!expanded[alerte.id]}
                          onToggle={() => toggleExpand(alerte.id)}
                        />
                      ))}
                    </div>
                  )}
                  {echeances.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
                        <CalendarClock size={13} /> Échéances à surveiller
                      </div>
                      {echeances.map(e => {
                        const dep = e.niveau === 'depasse';
                        const Icon = e.type === 'vaccin' ? Syringe : e.type === 'contrat' ? CalendarClock : AlertTriangle;
                        return (
                          <div key={e.id} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${dep ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                            <Icon size={16} className={`flex-shrink-0 ${dep ? 'text-red-500' : 'text-amber-600'}`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-[13.5px] font-medium text-slate-800 truncate">{e.libelle || e.type}{e.enfant_nom ? ` — ${e.enfant_nom}` : ''}</div>
                              <div className="text-[11.5px] text-slate-500 truncate">{e.structure_nom || ''}{e.date_echeance ? ` · ${new Date(e.date_echeance).toLocaleDateString('fr-FR')}` : ''}</div>
                            </div>
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${dep ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                              {dep ? `Retard ${Math.abs(e.jours_restants)} j` : `Dans ${e.jours_restants} j`}
                            </span>
                          </div>
                        );
                      })}
                      <a href="/echeances" className="block text-center text-[12px] text-navy-500 hover:text-navy-800 pt-1">Voir toutes les échéances →</a>
                    </div>
                  )}
                  {planningMods.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
                        <CalendarRange size={13} /> Modifications du planning équipe
                      </div>
                      {planningMods.map(m => {
                        const ajout = m.action === 'ajout';
                        return (
                          <div key={m.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${ajout ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-500'}`}>
                              {ajout ? <Plus size={13} /> : <Minus size={13} />}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-[13.5px] text-slate-800 truncate">
                                {ajout ? 'Créneau ajouté' : 'Créneau retiré'} — <span className="font-medium">{m.employe_nom || 'Salarié'}</span>
                              </div>
                              <div className="text-[11.5px] text-slate-500 truncate">
                                {m.date_slot ? new Date(m.date_slot).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }) : ''}{m.creneau ? ` · ${m.creneau}` : ''}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <a href="/rh" className="block text-center text-[12px] text-navy-500 hover:text-navy-800 pt-1">Voir le planning équipe →</a>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
              <p className="text-xs text-slate-400">Sources : Légifrance, CAF, ANSES, Ministères</p>
              <a href="/alertes" className="text-xs text-navy-500 hover:text-navy-800">
                Gérer les alertes →
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
